// Calendar dates. A coverage date is a day where the broker is, not an instant,
// so "today" is read from the local clock; `toISOString()` would be the UTC
// day, a day ahead every US evening. ISO YYYY-MM-DD strings compare correctly
// as strings, so callers compare them directly.

/** Today's local calendar date, as ISO YYYY-MM-DD (D-10's wire form). */
export function localTodayIso(now: Date = new Date()): string {
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

/** Today's UTC calendar date, as ISO YYYY-MM-DD: PMS's own today
 *  (`todayIso()`), for what PMS decides by date, such as whether a quote is
 *  still valid (PMS-S816, BRK-S462). Not a coverage date: those are local. */
export function utcTodayIso(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** An ISO date moved by whole months, on the calendar: a day the month it
 *  lands in doesn't have becomes that month's last (Mar 31 − 1 month is Feb
 *  28). Empty for anything that isn't an ISO date. */
export function shiftIsoMonths(iso: string, months: number): string {
  const match = ISO_DATE.exec(iso);
  if (!match) return "";
  const [, y, m, d] = match.map(Number);
  const first = new Date(Date.UTC(y, m - 1 + months, 1));
  const lastDay = new Date(
    Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0),
  ).getUTCDate();
  const month = String(first.getUTCMonth() + 1).padStart(2, "0");
  const day = String(Math.min(d, lastDay)).padStart(2, "0");
  return `${first.getUTCFullYear()}-${month}-${day}`;
}

/** An ISO date moved by whole years: a leap day lands on Feb 28. */
export function shiftIsoYears(iso: string, years: number): string {
  return shiftIsoMonths(iso, years * 12);
}
